import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CourseService } from '@lx/quote-tool/shared/data-access/course';
import { OnlineClassCombo } from '@lx/quote-tool/shared/types';
import { OffsetTimePipe } from '@lx/quote-tool/shared/ui/pipes';
import { convertTimeWIthOffsetToString } from '@lx/shared/utils';
import { ListboxModule } from 'primeng/listbox';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { DialogBasedSelector } from '@lx/quote-tool/lite/shared/ui/directives';
import { ClassTime } from '@lx/quote-tool/lite/online/types';
import { ListBoxComponent, SelectorFieldContainerComponent, SidePanelComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { ClassTimeSelectorData } from './class-time-selector-data.type';

/**
 * Component for selecting class times in online courses
 * Displays available class time options based on selected school, course, and timezone
 */
@Component({
  standalone: true,
  selector: 'lx-class-time-selector-field',
  templateUrl: './class-time-selector-field.component.html',
  styleUrls: ['./class-time-selector-field.component.scss'],
  imports: [SidePanelComponent, SelectorFieldContainerComponent, ListboxModule, FormsModule, CommonModule, OffsetTimePipe, ListBoxComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ClassTimeSelectorFieldComponent),
      multi: true,
    },
  ],
})
export class ClassTimeSelectorFieldComponent extends DialogBasedSelector<ClassTime> implements OnInit {
  /**
   * Observable that emits the selected school location, course, and timezone data
   * Required for fetching available class times
   */
  @Input({ required: true }) selectedSchoolLocationAndCourseAndTimezone$: Observable<ClassTimeSelectorData> | undefined;

  @Input() label: string | null = null;
  @Input() labelVisible = true;

  protected onlineCourses$: Observable<ClassTime[]> | undefined;
  protected selectedTimezoneOffset: number | undefined;
  protected readonly classTimeLabel = $localize`Class time`;
  protected readonly firstOptionLabel = $localize`I will decide later`;

  private readonly courseService = inject(CourseService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    if (!this.selectedSchoolLocationAndCourseAndTimezone$) {
      throw new Error('selectedSchoolLocationAndCourseAndTimezone$ input is required');
    }
    this.onlineCourses$ = this.getOnlineCourses();
  }

  /**
   * Triggers the selected class time changed event
   * Called when user selects a class time from the list
   */
  protected triggerSelectedClassTimeChanged(): void {
    this.handleSelectedValue({ value: this.selectedValue, fromUser: true });
  }

  /**
   * Gets the observable stream of available class times
   * @private
   */
  private getOnlineCourses(): Observable<ClassTime[]> {
    return this.selectedSchoolLocationAndCourseAndTimezone$!.pipe(
      tap(({ selectedTimezone }) => (this.selectedTimezoneOffset = selectedTimezone?.timezoneOffset)),
      switchMap(({ selectedSchoolLocation, selectedCourse }) => {
        if (!!selectedSchoolLocation && !!selectedCourse) {
          return this.courseService.getOnlineClassCombos(selectedCourse.code, selectedSchoolLocation?.schoolCode);
        }
        return of([]);
      }),
      map((courses: OnlineClassCombo[]) => [this.getFirstOption()].concat(courses.map((course) => this.getClassTime(course)))),
      tap((classTimes: ClassTime[]) => {
        if (!this.selectedValue) {
          this.selectedValue = this.getFirstOption();
          this.handleSelectedValue({ value: this.selectedValue, fromUser: true });
        }
        this.resetValueHandler(classTimes);
      }),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  /**
   * Resets the selected value if it's no longer in the available class times list
   * @param classTimes - The list of available class times to check against
   * @private
   */
  private resetValueHandler(classTimes: ClassTime[]): void {
    // Reset value if the previous selected value is not in the classTime list anymore
    if (classTimes.length) {
      const selectedValueInvalid = !classTimes.some(
        (classTime) => classTime.startTime === this.selectedValue?.startTime && classTime.endTime === this.selectedValue?.endTime,
      );
      if (selectedValueInvalid) {
        this.handleSelectedValue({ value: classTimes[0], fromUser: false });
      }
    }
  }

  /**
   * Gets the default "I will decide later" option
   * @private
   */
  private getFirstOption(): ClassTime {
    return {
      label: this.firstOptionLabel,
      startTime: '',
      endTime: '',
    };
  }

  /**
   * Converts an OnlineClassCombo to a ClassTime object
   * @param onlineClassCombo - The online class combo data from the API
   * @private
   */
  private getClassTime(onlineClassCombo: OnlineClassCombo): ClassTime {
    const { startTime, endTime } = onlineClassCombo.onlineClasses[0];

    return {
      label: `${convertTimeWIthOffsetToString(startTime, this.selectedTimezoneOffset)} - ${convertTimeWIthOffsetToString(
        endTime,
        this.selectedTimezoneOffset,
      )}`,
      startTime,
      endTime,
    };
  }
}
